import { CancellationNonResident } from 'cancellations/view-models/cancellation-non-resident';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { SubjectData } from 'shell/models/person';
import { transformSubjectAttributes } from 'shell/utils/consult-person/response-transformer/registry-office/subject-attributes';
import { transformBirthdayDocument } from 'shell/utils/consult-person/response-transformer/registry-office/birthday-document';
import { transformCitizenship } from 'shell/utils/consult-person/response-transformer/registry-office/citizenship';
import { transformOtherCitizenship } from 'shell/utils/consult-person/response-transformer/registry-office/other-citizenship';
import { transformParenthood } from 'shell/utils/consult-person/response-transformer/registry-office/parenthood';
import { DateTime } from 'luxon';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { transformFamilyCoexistence } from 'shell/utils/consult-person/response-transformer/family/family-coexistence';
import { ActType } from 'shell/constants/act-type';
import { emptyPaper } from 'shell/components/shared/act/constants';
import { transformDocumentDigital } from 'shell/utils/consult-person/request-transformer/shared/transform-document-digital';
import { CodeStepButtonsCancellation } from 'cancellations/constants/code-step-buttons-cancellation';
import {
  ChangeControlDataForm,
  DeathDataForm,
} from 'cancellations/view-models/cancellation-due-death';

const emptyChangeControlDataForm: ChangeControlDataForm = {
  cancellationReason: '',
  effectiveDate: DateTime.now(),
};

const emptyDeathDataForm: DeathDataForm = {
  actType: ActType.AttoCartaceo,
  paper: emptyPaper,
  eventDate: {
    type: ['day', 'month', 'year'],
    date: null,
  },
  eventForeign: null,
  eventItaly: null,
  exceptionalPlace: '',
  happenInItaly: false,
  office: '',
};

export const emptyItalianAuthority = {
  municipalityId: '',
  municipalityDescription: '',
  province: '',
  provinceCode: '',
  location: '',
};

export const emptyForeignAuthority = {
  stateId: '',
  stateDescription: '',
  municipalityId: '',
  municipalityDescription: '',
  province: '',
  istatCode: '',
};

export const transformAnprResponse = (response: SubjectData[]): CancellationNonResident => {
  const responseToTransform = response || {};
  const subjectData = responseToTransform[0] || {};
  //const headingResponse = responseToTransform.testataRisposta;
  const personForm = transformPerson(subjectData);
  const familyCoexistence = transformFamilyCoexistence(subjectData);
  const changeControlDataForm = emptyChangeControlDataForm;
  const deathDataForm = emptyDeathDataForm;
  const generalityForm = transformGenerality(subjectData);
  const subjectAttributesForm = transformSubjectAttributes(subjectData);
  const birthdayDocumentForm = transformBirthdayDocument(subjectData);
  const citizenshipForm = transformCitizenship(subjectData);
  const otherCitizenship = transformOtherCitizenship(subjectData);
  const parenthoodForm = transformParenthood(subjectData);

  return {
    activeStep: CodeStepButtonsCancellation.datiCancellazione,
    subjectData: {
      ...subjectData,
      matrimonio: {
        ...subjectData.matrimonio,
        coniuge: {
          ...subjectData.matrimonio?.coniuge,
        },
      },
    },
    person: personForm,
    changeControlDataForm,
    deathDataForm,
    generalityForm,
    subjectAttributesForm,
    birthdayDocumentForm,
    citizenship: citizenshipForm,
    otherCitizenship,
    parenthoodForm,
    familyCoexistence,
  };
};

export const transformAnprRequestDeath = (
  form: CancellationNonResident,
  anprResponse: SubjectData,
  fiscalCode: string
): unknown => {
  const withActPaper =
    form.deathDataForm?.office ||
    form.deathDataForm?.paper?.actYear ||
    form.deathDataForm?.paper?.actPart ||
    form.deathDataForm?.paper?.actSeries ||
    form.deathDataForm?.paper?.actNumber ||
    form.deathDataForm?.paper?.actVolume ||
    form.deathDataForm?.paper?.actDate ||
    form.deathDataForm?.paper?.actMunicipality?.description ||
    form.deathDataForm?.paper?.actMunicipality?.id ||
    form.deathDataForm?.paper?.actMunicipality?.province;

  const withActDigital = Boolean(form.deathDataForm?.digital);

  return {
    richiesta2001: {
      altriDati: {
        condizioneNonProfessionale: form.professionForm?.unprofessionalCondition || undefined,
        posizioneProfessionale: form.professionForm?.professionalPosition || undefined,
        titoloStudio: form.professionForm?.educationalQualification || undefined,
      },
      datiControllo: {
        ruoloSoggetto: anprResponse.legameSoggetto?.codiceLegame === '1' ? '1' : '2',
      },
      datiSentenza:
        form.judgmentDataForm?.authority ||
        form.judgmentDataForm?.judgmentDate ||
        form.judgmentDataForm?.validityDate ||
        form.judgmentDataForm?.courtType ||
        form.judgmentDataForm?.judgmentNumber
          ? {
              autorita: form.judgmentDataForm?.authority || undefined,
              data: form.judgmentDataForm?.judgmentDate?.toFormat('yyyy-MM-dd') || undefined,
              dataValidita:
                form.judgmentDataForm?.validityDate?.toFormat('yyyy-MM-dd') || undefined,
              tipoTribunale: form.judgmentDataForm?.courtType || undefined,
              numero: form.judgmentDataForm?.judgmentNumber || undefined,
            }
          : undefined,
      datiDecesso: {
        attoEventoANPR:
          withActPaper || withActDigital
            ? {
                atto: withActPaper
                  ? {
                      ufficioMunicipio: form.deathDataForm?.office || undefined,
                      anno: form.deathDataForm?.paper?.actYear || undefined,
                      parte: form.deathDataForm?.paper?.actPart || undefined,
                      serie: form.deathDataForm?.paper?.actSeries || undefined,
                      numeroAtto: form.deathDataForm?.paper?.actNumber || undefined,
                      volume: form.deathDataForm?.paper?.actVolume || undefined,
                      dataFormazioneAtto:
                        form.deathDataForm?.paper?.actDate?.toFormat('yyyy-MM-dd') || undefined,
                      trascritto: form.deathDataForm?.paper?.transcribed ? '1' : undefined,
                      comuneRegistrazione: form.deathDataForm?.paper?.actMunicipality
                        ? {
                            nomeComune:
                              form.deathDataForm?.paper?.actMunicipality?.description || undefined,
                            codiceIstat:
                              form.deathDataForm?.paper?.actMunicipality?.id || undefined,
                            siglaProvinciaIstat:
                              form.deathDataForm?.paper?.actMunicipality?.province || undefined,
                          }
                        : undefined,
                    }
                  : undefined,
                attoANSC: withActDigital
                  ? transformDocumentDigital(form.deathDataForm?.digital || undefined)
                  : undefined,
              }
            : undefined,
        dataEvento: form.deathDataForm?.eventDate?.date?.toFormat('yyyy-MM-dd') || null,
        luogoEvento: {
          luogoEccezionale: form.deathDataForm?.exceptionalPlace || null,
          comune:
            !form.deathDataForm?.happenInItaly && form.deathDataForm?.eventItaly
              ? {
                  codiceIstat: form.deathDataForm?.eventItaly?.municipalityId || undefined,
                  descrizioneLocalita: form.deathDataForm?.eventItaly?.location || undefined,
                  nomeComune: form.deathDataForm?.eventItaly?.municipalityDescription || undefined,
                  siglaProvinciaIstat: form.deathDataForm?.eventItaly?.province || undefined,
                }
              : null,
          localita:
            form.deathDataForm?.happenInItaly && form.deathDataForm?.eventForeign
              ? {
                  codiceStato: form.deathDataForm?.eventForeign?.stateId || undefined,
                  descrizioneLocalita:
                    form.deathDataForm?.eventForeign?.municipalityDescription || undefined,
                  descrizioneStato: form.deathDataForm?.eventForeign?.stateDescription || undefined,
                  provinciaContea: form.deathDataForm?.eventForeign?.province || undefined,
                }
              : null,
        },
      },
      generalitaPerRicerca: {
        idSchedaSoggettoANPR: anprResponse.generalita?.idSchedaSoggettoANPR || undefined,
        nome: form.person?.name || undefined,
        cognome: form.person?.surname || undefined,
        codiceFiscale: {
          codFiscale: fiscalCode || undefined,
        },
        idSchedaSoggettoComune: {
          idSchedaSoggetto: form.generalityForm?.municipalityIdentifier || undefined,
          idSchedaSoggettoComuneIstat: form.generalityForm?.istatCode || undefined,
        },
      },
      motivoCancellazione: {
        tipoMotivoCancellazione:
          anprResponse.generalita?.soggettoAIRE !== 'S'
            ? form.changeControlDataForm?.cancellationReason
            : undefined,
        tipoMotivoCancellazioneAIRE:
          anprResponse.generalita?.soggettoAIRE === 'S'
            ? form.changeControlDataForm?.cancellationReason
            : undefined,
      },
      testataRichiesta: {
        dataDecorrenza:
          form.changeControlDataForm?.effectiveDate?.toFormat('yyyy-MM-dd') || undefined,
      },
    },
  };
};
